import {CurrencyPipe, registerLocaleData} from "@angular/common";
import localeDe from "@angular/common/locales/de";
import localeEn from "@angular/common/locales/en";
import {provideHttpClient, withInterceptors, withXhr} from "@angular/common/http";
import {ApplicationConfig, ErrorHandler, LOCALE_ID, importProvidersFrom, provideZonelessChangeDetection} from "@angular/core";
import {DateAdapter, MAT_DATE_FORMATS} from "@angular/material/core";
import {MatDatepickerModule} from "@angular/material/datepicker";
import {MatDialogModule} from "@angular/material/dialog";
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {RouteReuseStrategy, provideRouter} from "@angular/router";
import {DayjsDateAdapter, DAYJS_DATE_FORMATS} from "@app/shared/adapters/dayjs-date-adapter";
import {defaultOAuthInterceptorFn} from "./shared/authentication/oauth/interceptors/default-oauth.interceptor";
import {TranslateModule} from "@ngx-translate/core";

import {APP_ROUTES} from "./app.routes";
import {AuthenticationGuard} from "./core/authentication/authentication.guard";
import {CustomRouteReuseStrategy} from "./core/custom-route-reusable-stragey";
import {DatadogErrorHandler} from "./core/datadog-error-handler";
import {apiPrefixInterceptorFn} from "./core/http/api-prefix.interceptor";
import {authorizationInterceptorFn} from "./core/http/authorization.interceptor";
import {cacheInterceptorFn} from "./core/http/cache.interceptor";
import {contentLocaleInterceptorFn} from "./core/http/content-locale.interceptor";
import {errorHandlerInterceptorFn} from "./core/http/error-handler.interceptor";
import {HttpCacheService} from "./core/http/http-cache.service";
import {I18nService} from "./core/i18n.service";
import {AppRoute} from "./shared/app.route.enum";
import {provideHypecastAuthentication} from "./shared/authentication/authentication.providers";
import {HypecastBaseOAuthConfig} from "./shared/authentication/models/HypecastBaseOAuthConfig";
import {HYPECAST_OAUTH_CONFIG} from "./shared/authentication/tokens/hypecast-oauth-config.token";
import {AudioPlayerService} from "./shared/components/audio-player-control/audio-player.service";
import {APP_ROUTE, APP_TITLE, BreadcrumbsService} from "./shared/components/breadcrumbs/breadcrumbs.service";
import {PipService} from "./shared/components/video-player/pip.service";
import {MinuteSecondsPipe} from "./shared/pipes/minute-seconds.pipe";
import {FormDeactiviationGuard} from "./shared/route-guards/form-deactivateable/form-deactiviation.guard";
import {ApiUrlService} from "./shared/services/api-url.service";
import {FilesService} from "./shared/services/files.service";
import {ForbiddenService} from "./shared/services/forbidden.service";
import {ITenantService} from "./shared/services/itenant.service";
import {MetricsService} from "./shared/services/metrics.service";
import {LoadingBarService} from "./shared/services/local/loading-bar/loading-bar.service";
import {TenantService} from "./shared/services/tenant.service";
import {TranscriptService} from "./shared/services/transcript.service";
import {UsersService} from "./shared/services/users.service";

registerLocaleData(localeDe);
registerLocaleData(localeEn);

function getCurrentLanguage(i18nService: I18nService): string | undefined {
  return i18nService.language;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(APP_ROUTES),
    // Angular's default XHR backend is intentional here; OAuth uses a private
    // HttpClient over HttpBackend so discovery/token traffic bypasses this chain.
    provideHttpClient(
      withXhr(),
      withInterceptors([
        contentLocaleInterceptorFn,
        apiPrefixInterceptorFn,
        errorHandlerInterceptorFn,
        defaultOAuthInterceptorFn,
        authorizationInterceptorFn,
        cacheInterceptorFn,
      ]),
    ),
    importProvidersFrom(
      TranslateModule.forRoot(),
      MatDatepickerModule,
      MatDialogModule,
      MatSnackBarModule,
    ),
    provideHypecastAuthentication(),
    MinuteSecondsPipe,
    TranscriptService,
    {provide: ITenantService, useClass: TenantService},
    ApiUrlService,
    ForbiddenService,
    FormDeactiviationGuard,
    AuthenticationGuard,
    HttpCacheService,
    LoadingBarService,
    UsersService,
    AudioPlayerService,
    // IframePluginService is providedIn: "root" and injects these two. Before the
    // standalone migration they reached the root injector through eagerly imported
    // NgModules; route-scoped providers alone leave the root instantiation
    // unresolvable (NG0201, release 0.0.18). Plugin routes still provide their own
    // instances via COYO_PLUGIN_PROVIDERS.
    MetricsService,
    FilesService,
    PipService,
    I18nService,
    {provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy},
    BreadcrumbsService,
    CurrencyPipe,
    {provide: MAT_DATE_FORMATS, useValue: DAYJS_DATE_FORMATS},
    {provide: APP_ROUTE, useValue: AppRoute},
    {provide: APP_TITLE, useValue: "Hypecast"},
    {provide: LOCALE_ID, useFactory: getCurrentLanguage, deps: [I18nService]},
    {provide: DateAdapter, useClass: DayjsDateAdapter},
    {provide: HYPECAST_OAUTH_CONFIG, useValue: new HypecastBaseOAuthConfig("test")},
    {provide: ErrorHandler, useClass: DatadogErrorHandler},
  ],
};
